import { Node, nodeInputRule } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { pickBy } from "remeda"
import { initImageZoom } from "../image-zoom.js"

export const IMAGE_NODE = "image"

/**
 * Matches an image to a ![image](src "title") on input.
 */
const inputRegex = /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/

export const ImageNode = Node.create({
  name: IMAGE_NODE,
  group: "block",
  inline: false,
  draggable: true,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: element =>
          element.querySelector("img")?.getAttribute("src") ?? undefined,
      },
      alt: {
        default: null,
        parseHTML: element =>
          element.querySelector("img")?.getAttribute("alt") ?? undefined,
      },
      title: {
        default: null,
        parseHTML: element =>
          element.querySelector("img")?.getAttribute("title"),
      },
      size: {
        default: "medium",
        parseHTML: element => element.getAttribute("data-size"),
        renderHTML: attributes =>
          attributes.size ? { "data-size": attributes.size } : {},
      },
      // TODO: deprecate this, already in unique id tiptap extension
      uid: {
        default: null,
        parseHTML: element => element.getAttribute("data-uid"),
        renderHTML: attributes =>
          attributes.uid ? { "data-uid": attributes.uid } : {},
      },
      width: {
        default: null,
        parseHTML: element => {
          const w = element.querySelector("img")?.getAttribute("width")
          return w ? Number(w) : null
        },
      },
      height: {
        default: null,
        parseHTML: element => {
          const h = element.querySelector("img")?.getAttribute("height")
          return h ? Number(h) : null
        },
      },
    }
  },

  parseHTML() {
    return [{ tag: `figure[data-type="${IMAGE_NODE}"]` }]
  },

  renderHTML({ HTMLAttributes }) {
    const { "data-size": size, ...rest } = HTMLAttributes
    const imgAttrs = pickBy(rest, val => !!val)
    const figureAttrs: Record<string, string> = { "data-type": IMAGE_NODE }

    if (size) {
      figureAttrs["data-size"] = size as string
    }

    if (!imgAttrs.title) return ["figure", figureAttrs, ["img", imgAttrs]]

    return [
      "figure",
      figureAttrs,
      ["img", imgAttrs],
      ["figcaption", {}, imgAttrs.title as string],
    ]
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: match => {
          const [, , alt, src, title] = match
          return { src, alt, title }
        },
      }),
    ]
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("imageZoom"),
        view() {
          initImageZoom()
          return {}
        },
      }),
    ]
  },
})
